import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get, post, put, remove } from "../../../utils/apiUtils";

/**
 * Replace this with your real API client (axios/fetch wrapper).
 * Example:
 *   import api from "../../../api/api";
 */
const api = {
  get: async (url: string, config?: any) => {
    const res = await fetch(buildUrl(url, config?.params), { method: "GET" });
    if (!res.ok) throw new Error(await safeText(res));
    return { data: await res.json() };
  },
  delete: async (url: string) => {
    const res = await fetch(url, { method: "DELETE" });
    if (!res.ok) throw new Error(await safeText(res));
    return { data: await safeText(res) };
  },
  post: async (url: string, body: any) => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await safeText(res));
    return { data: await res.json() };
  },
};

function buildUrl(url: string, params?: Record<string, any>) {
  if (!params) return url;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}
async function safeText(res: Response) {
  try {
    return await res.text();
  } catch {
    return "Request failed";
  }
}

/** Types */
export type MemoItem = {
  id: string;
  title?: string;
  preview?: string;
  content?: string;
  createdAt?: string;
  createdBy?: string;
  groupId?: string | null;
  groupName?: string | null;
};
export type MemoDetails = {
  id: string;
  title: string;
  description: string;
  created_at?: string;
  created_by?: number | string;
  groups?: { id: string; name?: string }[];
};
export type MemoPreview = {
  title: string;
  description: string;
  linked_counts: Record<string, number>;
  group_count: number;
};
export type MemoGroup = {
  id: string;
  name: string;
  memo_count?: number;
};

export type LinkableEntityType =
  | "document"
  | "highlight"
  | "code"
  | "descriptor";
export type LinkStatus = "linked" | "unlinked" | "all";

export type LinkableEntity = {
  id: string; // entity_id
  type: LinkableEntityType;
  item?: string;
  name?: string;
  is_linked: boolean;

  // ✅ needed for DELETE /links/{link_id}
  // backend MUST return this when linked, otherwise unlink can't work
  link_id?: string | null;

  user?: string;
};

// type MemoState = {
//   projectId: string | null;

//   items: MemoItem[];
//   total: number;

//   page: number;
//   pageSize: number;

//   loading: boolean;
//   error: string | null;

//   // filters
//   search: string;
//   createdDate: string | null; // YYYY-MM-DD
//   groupId: string | null;

//   // groups dropdown
//   groups: MemoGroup[];
//   groupsLoading: boolean;

//   // delete
//   deletingId: string | null;
//   deletingLoading: boolean;
//   creating: boolean;

//   //add memo to group
//   creatingGroup: boolean;
//   creatingMemo: boolean;
//   addingToGroup: boolean;

//   // memo previews
//   previewById: Record<string, MemoPreview | undefined>;
//   previewLoadingById: Record<string, boolean | undefined>;
//   previewErrorById: Record<string, string | undefined>;
// };

// const initialState: MemoState = {
//   projectId: null,

//   items: [],
//   total: 0,

//   page: 1,
//   pageSize: 9,

//   loading: false,
//   error: null,

//   search: "",
//   createdDate: null,
//   groupId: null,

//   groups: [],
//   groupsLoading: false,

//   deletingId: null,
//   deletingLoading: false,
//   creating: false,
//   previewById: {},
//   previewLoadingById: {},
//   previewErrorById: {},

//   creatingGroup: false,
//   creatingMemo: false,
//   addingToGroup: false,
// };

type MemoState = {
  projectId: string | null;

  items: MemoItem[];
  total: number;

  page: number;
  pageSize: number;

  loading: boolean;
  error: string | null;

  search: string;
  createdDate: string | null;
  groupId: string | null;

  groups: MemoGroup[];
  groupsLoading: boolean;
  updatingMemo: boolean;
  deletingId: string | null;
  deletingLoading: boolean;

  // ✅ separated loaders
  creatingGroup: boolean;
  creatingMemo: boolean;
  addingToGroup: boolean;

  // memo previews
  previewById: Record<string, MemoPreview | undefined>;
  previewLoadingById: Record<string, boolean | undefined>;
  previewErrorById: Record<string, string | undefined>;

  //memodetails
  detailsById: Record<string, MemoDetails | undefined>;
  detailsLoadingById: Record<string, boolean | undefined>;
  detailsErrorById: Record<string, string | undefined>;

  // linkable entities (Linked Media modal)
  // linkable entities (Linked Media modal)
  linkableItems: LinkableEntity[];
  linkableTotal: number;
  linkableLoading: boolean;
  linkableError: string | null;
  linkableMeta: {
    memoId: string | null;
    entity_type: LinkableEntityType | null;
    search: string;
    link_status: LinkStatus;
    skip: number;
    limit: number;
  };
};

const initialState: MemoState = {
  projectId: null,
  items: [],
  total: 0,
  page: 1,
  pageSize: 9,

  loading: false,
  error: null,

  search: "",
  createdDate: null,
  groupId: null,
  updatingMemo: false,

  groups: [],
  groupsLoading: false,

  deletingId: null,
  deletingLoading: false,

  creatingGroup: false,
  creatingMemo: false,
  addingToGroup: false,

  previewById: {},
  previewLoadingById: {},
  previewErrorById: {},

  detailsById: {},
  detailsLoadingById: {},
  detailsErrorById: {},

  linkableItems: [],
  linkableTotal: 0,
  linkableLoading: false,
  linkableError: null,
  linkableMeta: {
    memoId: null,
    entity_type: null,
    search: "",
    link_status: "all",
    skip: 0,
    limit: 20,
  },
};

/**
 * Fetch memos (list) - similar to fetchMedia
 * Expected API response shape (adjust if needed):
 * {
 *   items: MemoItem[],
 *   total: number,
 *   groups?: MemoGroup[]   // optional if backend returns groups together
 * }
 */

export const fetchMemos = createAsyncThunk(
  "memo/fetchMemos",
  async (_, thunkAPI) => {
    const s: MemoState = (thunkAPI.getState() as any).memo;
    if (!s.projectId) return { items: [], total: 0, groups: [] };

    const skip = Math.max(0, (s.page - 1) * s.pageSize);
    const limit = s.pageSize;

    const params: Record<string, any> = {
      project_id: s.projectId,
      search: s.search || undefined,
      group_id: s.groupId || undefined,
      skip,
      limit,
    };

    if (s.createdDate) {
      params.date_from = s.createdDate;
      params.date_to = s.createdDate;
    }

    // ✅ utils get() returns ApiResponse<T>
    const res = await get<any>("/api/memos", { params });

    if (res.type === "error") {
      // makes rejected action with message in extraReducers
      return thunkAPI.rejectWithValue(
        (res.data as any)?.message || "Failed to fetch memos"
      );
    }

    const payload = res.data; // <-- actual backend payload

    const rawItems = payload?.items ?? [];
    const total = payload?.total ?? rawItems.length;

    const items: MemoItem[] = rawItems.map((m: any) => ({
      id: m.id,
      title: m.title,
      preview: m.description ?? "",
      content: m.description ?? "",
      createdAt: m.created_at,
      createdBy: String(m.created_by ?? ""),
      // groups: m.groups ?? [], // (optional)
    }));

    const groupsFromApi = payload?.groups;

    return {
      items,
      total,
      ...(Array.isArray(groupsFromApi) ? { groups: groupsFromApi } : {}),
    };
  }
);

export const fetchMemoDetails = createAsyncThunk<
  { memoId: string; details: MemoDetails },
  { memoId: string },
  { rejectValue: string }
>("memo/fetchMemoDetails", async ({ memoId }, thunkAPI) => {
  const res = await get<any>(`/api/memos/${memoId}`);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to fetch memo details"
    );
  }

  const m = res.data;

  return {
    memoId,
    details: {
      id: m?.id,
      title: m?.title ?? "",
      description: m?.description ?? "",
      created_at: m?.created_at,
      created_by: m?.created_by,
      groups: Array.isArray(m?.groups) ? m.groups : [],
    },
  };
});

/** Add memo to groups (NEW endpoint - matches Figma) */
export const addMemoToGroups = createAsyncThunk<
  void,
  { memoId: string; groupIds: string[] },
  { rejectValue: string }
>("memo/addMemoToGroups", async ({ memoId, groupIds }, thunkAPI) => {
  const body = { group_ids: groupIds };
  const res = await post<any>(`/api/memos/${memoId}/groups`, body);
  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to add memo to groups"
    );
  }
});

/** Fetch memo preview */
export const fetchMemoPreview = createAsyncThunk<
  { memoId: string; preview: MemoPreview },
  { memoId: string },
  { rejectValue: string }
>("memo/fetchMemoPreview", async ({ memoId }, thunkAPI) => {
  const res = await get<any>(`/api/memos/${memoId}/preview`);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to fetch memo preview"
    );
  }

  const p = res.data;

  return {
    memoId,
    preview: {
      title: p?.title ?? "",
      description: p?.description ?? "",
      linked_counts: p?.linked_counts ?? {},
      group_count: p?.group_count ?? 0,
    },
  };
});

/**
 * Fetch memo groups for dropdown
 * If your backend already returns groups inside fetchMemos, you can skip using this.
 */
export const fetchMemoGroups = createAsyncThunk<
  MemoGroup[],
  void,
  { state: any; rejectValue: string }
>("memo/fetchMemoGroups", async (_, thunkAPI) => {
  const s: MemoState = thunkAPI.getState().memo;

  if (!s.projectId) return [];

  const res = await get<any>("/api/memo-groups", {
    params: { project_id: s.projectId }, // ✅ KJO MUNGONTE
  });

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to fetch memo groups"
    );
  }

  const payload = res.data;

  const raw = Array.isArray(payload)
    ? payload
    : payload?.items ?? payload?.data ?? [];

  return raw.map((g: any) => ({
    id: g.id,
    name: g.name,
    memo_count: g.memo_count ?? 0,
  }));
});

//Create Memo//

export const createMemo = createAsyncThunk<
  any,
  { projectId: string; title: string; description: string; groupIds: string[] },
  { rejectValue: string }
>("memo/createMemo", async (payload, thunkAPI) => {
  const body = {
    title: payload.title,
    description: payload.description,
    project_id: payload.projectId,
    group_ids: payload.groupIds ?? [],
  };

  const res = await post<any>("/api/memos", body);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to create memo"
    );
  }

  return res.data; // created memo object
});

/**Uupdate memo */
export const updateMemo = createAsyncThunk<
  any,
  { memoId: string; title: string; description: string; groupIds: string[] },
  { rejectValue: string }
>(
  "memo/updateMemo",
  async ({ memoId, title, description, groupIds }, thunkAPI) => {
    const body = {
      title,
      description,
      group_ids: groupIds ?? [],
    };

    const res = await put<any>(`/api/memos/${memoId}`, body);

    if (res.type === "error") {
      return thunkAPI.rejectWithValue(
        (res.data as any)?.message || "Failed to update memo"
      );
    }

    return res.data;
  }
);

/** Create group memo*/
export const createMemoGroups = createAsyncThunk<
  MemoGroup[],
  { projectId: string; names: string[] },
  { rejectValue: string }
>("memo/createMemoGroups", async ({ projectId, names }, thunkAPI) => {
  const body = {
    project_id: projectId,
    names,
  };

  const res = await post<any>("/api/memo-groups", body);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to create memo groups"
    );
  }

  const payload = res.data;

  // swagger response looks like array of groups
  const raw = Array.isArray(payload)
    ? payload
    : payload?.items ?? payload?.data ?? [];

  return raw.map((g: any) => ({
    id: g.id,
    name: g.name,
    memo_count: g.memo_count ?? 0,
  }));
});

/**
 * Delete memo
 */
export const deleteMemo = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>("memo/deleteMemo", async (memoId, thunkAPI) => {
  const res = await remove<any>(`/api/memos/${memoId}`);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to delete memo"
    );
  }
});

/**
 * (Optional) Create group – if you want the modal to be real, not static
 */
export const createMemoGroup = createAsyncThunk<
  MemoGroup,
  { projectId: string; name: string }
>("memo/createMemoGroup", async ({ projectId, name }) => {
  // ✅ Adjust endpoint to your backend
  const { data } = await api.post("/memo-groups", {
    project_id: projectId,
    name,
  });
  return data;
});

/** Fetch linkable entities for a memo (Linked Media modal) */
export const fetchLinkableEntities = createAsyncThunk<
  { items: LinkableEntity[]; total: number; meta: MemoState["linkableMeta"] },
  {
    memoId: string;
    entity_type?: LinkableEntityType | "";
    search?: string;
    link_status?: LinkStatus;
    skip?: number;
    limit?: number;
  },
  { rejectValue: string }
>("memo/fetchLinkableEntities", async (args, thunkAPI) => {
  const {
    memoId,
    entity_type,
    search,
    link_status = "all",
    skip = 0,
    limit = 20,
  } = args;

  const params: Record<string, any> = {
    link_status,
    skip,
    limit,
    entity_type: entity_type || undefined,
    search: search || undefined,
  };

  const res = await get<any>(`/api/memos/${memoId}/linkable-entities`, {
    params,
  });

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to fetch linkable entities"
    );
  }

  const payload = res.data;
  const rawItems = payload?.items ?? [];
  const total = payload?.total ?? rawItems.length;

  const items: LinkableEntity[] = rawItems.map((x: any) => ({
    id: x.id,
    type: x.type,
    item: x.item,
    name: x.name,
    is_linked: !!x.is_linked,
    link_id: x.link_id ?? null,
    user: x.user,
  }));

  return {
    items,
    total,
    meta: {
      memoId,
      entity_type: (entity_type as any) || null,
      search: search || "",
      link_status,
      skip,
      limit,
    },
  };
});

export const linkMemoEntity = createAsyncThunk<
  void,
  { memoId: string; linked_type: LinkableEntityType; linked_entity_id: string },
  { rejectValue: string }
>(
  "memo/linkMemoEntity",
  async ({ memoId, linked_type, linked_entity_id }, thunkAPI) => {
    const res = await post<any>(`/api/memos/${memoId}/links`, {
      linked_type,
      linked_entity_id,
    });

    if (res.type === "error") {
      return thunkAPI.rejectWithValue(
        (res.data as any)?.message || "Failed to link entity"
      );
    }
  }
);

export const unlinkMemoEntity = createAsyncThunk<
  void,
  { memoId: string; linkId: string },
  { rejectValue: string }
>("memo/unlinkMemoEntity", async ({ memoId, linkId }, thunkAPI) => {
  const res = await remove<any>(`/api/memos/${memoId}/links/${linkId}`);

  if (res.type === "error") {
    return thunkAPI.rejectWithValue(
      (res.data as any)?.message || "Failed to unlink entity"
    );
  }
});

const memoSlice = createSlice({
  name: "memo",
  initialState,
  reducers: {
    setMemoProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
      state.page = 1;
    },

    setMemoSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      state.page = 1;
    },
    setMemoCreatedDate(state, action: PayloadAction<string | null>) {
      state.createdDate = action.payload;
      state.page = 1;
    },
    setMemoGroupId(state, action: PayloadAction<string | null>) {
      state.groupId = action.payload;
      state.page = 1;
    },

    setMemoPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setMemoPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1;
    },

    setDeletingMemoId(state, action: PayloadAction<string | null>) {
      state.deletingId = action.payload;
    },
    resetLinkableEntities(state) {
      state.linkableItems = [];
      state.linkableTotal = 0;
      state.linkableLoading = false;
      state.linkableError = null;
      state.linkableMeta = {
        memoId: null,
        entity_type: null,
        search: "",
        link_status: "all",
        skip: 0,
        limit: 20,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchMemos
      .addCase(fetchMemos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMemos.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.total = action.payload.total ?? state.items.length;

        // if API returns groups too
        if (
          Array.isArray(action.payload.groups) &&
          action.payload.groups.length > 0
        ) {
          state.groups = action.payload.groups;
        }
      })
      .addCase(fetchMemos.rejected, (state, action: any) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to fetch memos";
      })
      //get memo details
      .addCase(fetchMemoDetails.pending, (state, action) => {
        const memoId = action.meta.arg.memoId;
        state.detailsLoadingById[memoId] = true;
        state.detailsErrorById[memoId] = undefined;
      })
      .addCase(fetchMemoDetails.fulfilled, (state, action) => {
        const { memoId, details } = action.payload;
        state.detailsLoadingById[memoId] = false;
        state.detailsById[memoId] = details;
      })
      .addCase(fetchMemoDetails.rejected, (state, action: any) => {
        const memoId = action.meta.arg.memoId;
        state.detailsLoadingById[memoId] = false;
        state.detailsErrorById[memoId] =
          action.payload ||
          action.error?.message ||
          "Failed to fetch memo details";
      })
      // update memo
      .addCase(updateMemo.pending, (state) => {
        state.updatingMemo = true;
        state.error = null;
      })
      .addCase(updateMemo.fulfilled, (state, action) => {
        state.updatingMemo = false;

        // ✅ update ne list (pa bo refetch nese s'don)
        const updated = action.payload;
        const idx = state.items.findIndex((m) => m.id === updated?.id);
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            title: updated?.title ?? state.items[idx].title,
            preview: updated?.description ?? state.items[idx].preview,
            content: updated?.description ?? state.items[idx].content,
          };
        }
      })
      .addCase(updateMemo.rejected, (state, action: any) => {
        state.updatingMemo = false;
        state.error =
          action.payload || action.error?.message || "Failed to update memo";
      })

      //fetch memo preview
      .addCase(fetchMemoPreview.pending, (state, action) => {
        const memoId = action.meta.arg.memoId;
        state.previewLoadingById[memoId] = true;
        state.previewErrorById[memoId] = undefined;
      })
      .addCase(fetchMemoPreview.fulfilled, (state, action) => {
        const { memoId, preview } = action.payload;
        state.previewLoadingById[memoId] = false;
        state.previewById[memoId] = preview;
      })
      .addCase(fetchMemoPreview.rejected, (state, action: any) => {
        const memoId = action.meta.arg.memoId;
        state.previewLoadingById[memoId] = false;
        state.previewErrorById[memoId] =
          action.payload ||
          action.error?.message ||
          "Failed to fetch memo preview";
      })

      // fetchMemoGroups
      .addCase(fetchMemoGroups.pending, (state) => {
        state.groupsLoading = true;
      })
      .addCase(fetchMemoGroups.fulfilled, (state, action) => {
        state.groupsLoading = false;
        state.groups = action.payload || [];
      })
      // .addCase(fetchMemoGroups.rejected, (state) => {
      //   state.groupsLoading = false;
      // })
      .addCase(fetchMemoGroups.rejected, (state, action: any) => {
        state.groupsLoading = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch memo groups";
      })

      // create memo
      .addCase(createMemo.pending, (state) => {
        state.creatingMemo = true;
        state.error = null;
      })
      .addCase(createMemo.fulfilled, (state) => {
        state.creatingMemo = false;
      })
      .addCase(createMemo.rejected, (state, action: any) => {
        state.creatingMemo = false;
        state.error =
          action.payload || action.error?.message || "Failed to create memo";
      })

      // deleteMemo
      .addCase(deleteMemo.pending, (state) => {
        state.deletingLoading = true;
      })
      .addCase(deleteMemo.fulfilled, (state) => {
        state.deletingLoading = false;
      })
      .addCase(deleteMemo.rejected, (state, action) => {
        state.deletingLoading = false;
        state.error =
          (action.error?.message as string) || "Failed to delete memo";
      })

      // addMemoToGroups
      .addCase(addMemoToGroups.pending, (state) => {
        state.addingToGroup = true;
        state.error = null;
      })
      .addCase(addMemoToGroups.fulfilled, (state) => {
        state.addingToGroup = false;
      })
      .addCase(addMemoToGroups.rejected, (state, action: any) => {
        state.addingToGroup = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to add memo to groups";
      })

      // create memo groups
      .addCase(createMemoGroups.pending, (state) => {
        state.creatingGroup = true;
        state.error = null;
      })
      .addCase(createMemoGroups.fulfilled, (state, action) => {
        state.creatingGroup = false;

        const existingIds = new Set(state.groups.map((g) => g.id));
        const merged = [...state.groups];

        for (const g of action.payload) {
          if (!existingIds.has(g.id)) merged.push(g);
        }
        state.groups = merged;
      })
      .addCase(createMemoGroups.rejected, (state, action: any) => {
        state.creatingGroup = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to create memo groups";
      })

      // fetchLinkableEntities
      .addCase(fetchLinkableEntities.pending, (state) => {
        state.linkableLoading = true;
        state.linkableError = null;
      })
      .addCase(fetchLinkableEntities.fulfilled, (state, action) => {
        state.linkableLoading = false;
        state.linkableItems = action.payload.items;
        state.linkableTotal = action.payload.total;
        state.linkableMeta = action.payload.meta;
      })
      .addCase(fetchLinkableEntities.rejected, (state, action: any) => {
        state.linkableLoading = false;
        state.linkableError =
          action.payload ||
          action.error?.message ||
          "Failed to fetch linkable entities";
      })
      .addCase(linkMemoEntity.rejected, (state, action: any) => {
        state.linkableError =
          action.payload || action.error?.message || "Failed to link entity";
      })
      .addCase(unlinkMemoEntity.rejected, (state, action: any) => {
        state.linkableError =
          action.payload || action.error?.message || "Failed to unlink entity";
      });
  },
});

export const {
  setMemoProjectId,
  setMemoSearch,
  setMemoCreatedDate,
  setMemoGroupId,
  setMemoPage,
  setMemoPageSize,
  setDeletingMemoId,
  resetLinkableEntities,
} = memoSlice.actions;

export default memoSlice.reducer;
